/* eslint-disable dot-notation */
import jsCookie from 'js-cookie';
import {
  login,
  logoutService,
  register,
  getAllUsers,
  updateUserService,
  adminUpdateUserService,
  getUserProfileService,
  newPasswordService,
  forgot,
  followUser,
  unfollowUser,
  getUser as getUserService,
  deleteUser as deleteUserService,
  updateZohoGroups as updateZohoGroupsService,
} from '../services/userService';
import { setAuthToken, clearAuthToken } from '../../services/getAuthToken';

import {
  SET_USER,
  SET_AUTHENTICATED,
  SET_UNAUTHENTICATED,
  SET_USERLOGGED_IN,
  SET_USERS,
  SET_USER_PROFILE,
  UPDATE_USER,
  FOLLOW_USER,
  UNFOLLOW_USER,
  USER_DELETED,
  //   MARK_NOTIFICATIONS_READ,
} from '../types/users';

import { SET_ERRORS, CLEAR_ERRORS, LOADING_UI, SET_SUCCESS } from '../types/ui';

export const updateZohoGroups = () => async () => {
  await updateZohoGroupsService();
};

// Returns one of:
//   null                                                  — session created, navigate home
//   { kind: 'challenge', challengeToken }                 — MFA on, go to second-step
//   { kind: 'cohortEnrolment', challengeToken, cohort }   — must enrol before logging in
//   { kind: 'error', message }                            — auth failed, show message
//
// The challenge branch is the W2 server change: when a user has MFA on,
// /login no longer returns a session — it returns a 15-min challenge
// token that the second-step page exchanges for a session via /mfa/verify.
//
// The cohortEnrolment branch is the W5 server change. Without it the response
// fell through to setAuthToken(undefined) and this action reported success, so
// the app "logged in" with no session and bounced straight back to /login —
// every member of an active cohort was silently locked out with no message.
export const loginUser = (userData) => async (dispatch) => {
  try {
    const response = await login(userData);
    if (response && response.mfaRequired === true) {
      dispatch({ type: CLEAR_ERRORS });
      return { kind: 'challenge', challengeToken: response.challengeToken };
    }
    if (response && response.enrolmentRequired === true) {
      dispatch({ type: CLEAR_ERRORS });
      return {
        kind: 'cohortEnrolment',
        challengeToken: response.challengeToken,
        cohort: response.cohort,
      };
    }
    // Web: server set the HttpOnly cookie; setAuthToken is a noop on web.
    // Mobile: store the raw JWT in Capacitor Preferences (no Bearer prefix —
    // legacy "Bearer <jwt>" stored values continue to work via stripBearer
    // on the server, but new sessions store the raw value).
    await setAuthToken(response.accessToken);
    dispatch({ type: CLEAR_ERRORS });
    return null;
  } catch (error) {
    return { kind: 'error', message: error.response?.data?.message || String(error) };
  }
};

export const registerUser = (registerNewUser) => async (dispatch) => {
  try {
    const response = await register(registerNewUser);
    dispatch({ type: SET_USER, payload: response.message });
    return { success: true, message: '' };
  } catch (error) {
    return { success: false, message: error.response?.data?.message };
  }
};

export const loggedInUser = () => (dispatch) => {
  try {
    const user = JSON.parse(localStorage.getItem('user'));

    dispatch({ type: CLEAR_ERRORS });
    dispatch({ type: SET_USERLOGGED_IN, payload: user });
  } catch (error) {
    dispatch({
      type: SET_ERRORS,
      payload: error.response?.data,
    });
  }
};

export const logoutUser = () => async () => {
  // Tell the server to clear the HttpOnly cookie. Best-effort — if the
  // request fails (network, already-expired token, server down), we still
  // proceed with local cleanup so the UI doesn't get stuck "logged in".
  try {
    await logoutService();
  } catch {
    /* ignored — local cleanup below still runs */
  }
  await clearAuthToken();
  window.location = '/login';
};

export const getAllTheUsers = () => async (dispatch) => {
  try {
    const response = await getAllUsers();
    dispatch({ type: SET_USERS, payload: response });
  } catch (error) {
    dispatch({
      type: SET_ERRORS,
      payload: error.response?.data,
    });
  }
};

export const adminUpdateUser = (userData) => async (dispatch) => {
  try {
    const { _id, ...fields } = userData;
    await adminUpdateUserService(_id, fields);
  } catch (error) {
    dispatch({
      type: SET_ERRORS,
      payload: error.response,
    });
  }
};

export const updateUser = (userData) => async (dispatch) => {
  try {
    await updateUserService(userData);
  } catch (error) {
    dispatch({
      type: SET_ERRORS,
      payload: error.response,
    });
  }
};

export const getUserProfile = (user) => async (dispatch) => {
  const userProfile = await getUserProfileService(user);
  try {
    dispatch({ type: CLEAR_ERRORS });
    dispatch({ type: SET_USER_PROFILE, payload: userProfile });
  } catch (error) {
    dispatch({
      type: SET_ERRORS,
      payload: error.response,
    });
  }
};

export const getUser = () => async (dispatch) => {
  try {
    const user = await getUserService();
    dispatch({ type: SET_AUTHENTICATED, payload: { user } });
    return user;
  } catch (error) {
    dispatch({
      type: SET_ERRORS,
      payload: error.response,
    });
  }
};

export const forgetAction = (email) => async (dispatch) => {
  try {
    const sucessMessage = await forgot(email);
    dispatch({ type: CLEAR_ERRORS });
    dispatch({ type: SET_SUCCESS, payload: sucessMessage });
  } catch (error) {
    dispatch({
      type: SET_ERRORS,
      payload: error.response,
    });
  }
};

export const newPasswordAction =
  (newPassword, confirmPassword, token) => async (dispatch) => {
    try {
      const { message } = await newPasswordService(
        newPassword,
        confirmPassword,
        token
      );
      dispatch({ type: CLEAR_ERRORS });
      dispatch({ type: SET_SUCCESS, payload: message });
      return message;
    } catch (error) {
      dispatch({
        type: SET_ERRORS,
        payload: error.response,
      });
      return error;
    }
  };

export const followThisUser = (userToFollow) => async (dispatch) => {
  try {
    const res = await followUser(userToFollow);
    dispatch({ type: CLEAR_ERRORS });
    dispatch({ type: FOLLOW_USER, payload: res.data });
  } catch (error) {
    dispatch({
      type: SET_ERRORS,
      payload: error.response,
    });
  }
};

export const unfollowThisUser = (userToFollow) => async (dispatch) => {
  try {
    const res = await unfollowUser(userToFollow);
    dispatch({ type: CLEAR_ERRORS });
    dispatch({ type: UNFOLLOW_USER, payload: res.data });
  } catch (error) {
    dispatch({
      type: SET_ERRORS,
      payload: error.response,
    });
  }
};

export const deleteUser = (userId) => async (dispatch) => {
  try {
    await deleteUserService(userId);
    dispatch({ type: CLEAR_ERRORS });
    dispatch({ type: USER_DELETED, payload: { userId } });
  } catch (error) {
    dispatch({
      type: SET_ERRORS,
      payload: error.response,
    });
  }
};
